import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { CogIcon } from "@heroicons/react/outline";
import timezones from "Data/timezones";
import {
    detailsForm,
    socialProfilesForm,
    inviteTeamMemberForm
} from "Data/accounts/AccountValidations";

import * as AccountActions from "Data/accounts/AccountActions";
import * as UserActions from "Data/users/UserActions";

import Form from "Components/Form";
import Container from 'Components/Container';
import SettingsPanel from "Components/SettingsPanel";

import Heading from "UI/Heading";
import {TextField} from "UI/Form";
import Button from "UI/Button/Button";
import TeamMemberList from "Components/TeamMemberList";
import Feature from "Components/Feature";
import {Redirect} from "react-router-dom";
import Combobox from "UI/Combobox";

class SettingsController extends React.Component
{
    state = {
        busy: false
    };

    static propTypes = {
        accountId: PropTypes.string.isRequired,
        loading: PropTypes.bool.isRequired,

        invites: PropTypes.object.isRequired,
        users: PropTypes.object.isRequired,

        account: PropTypes.object.isRequired,
        updateDetails: PropTypes.func.isRequired,
        invite: PropTypes.func.isRequired
    };

    onChangedDetails = (data) => {
        this.setState({busy: true}, () => {
            this.props.updateDetails(data.name, data.website, data.timezone).then(() => {
                this.setState({busy: false});
            });
        })
    };

    onInvite = (data, setData) => {
        this.setState({busy: true}, () => {
            this.props.invite(data.email).then(() => {
                setData({email: ''});
                this.setState({busy: false});
            })
        });
    };

    render()
    {
        const currentDetails = {
            name: this.props.account.name,
            website: this.props.account.website,
            timezone: this.props.account.timezone,
        }

        return <Container>

            <Feature when={features => ! features.settings}>
                <Redirect to='/' />
            </Feature>

            <Heading
                title={<div className='flex'><CogIcon className='w-7 mr-3' /> Settings </div>}
                description='Manage the information for your organisation.'
            />

            <SettingsPanel title='Details' description='Information about your account.'>
                <Form id='account-details' defaults={currentDetails} validate={detailsForm} onSubmit={this.onChangedDetails}>
                    {(data, onChange, validation) => (
                        <fieldset className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
                            <div className="sm:col-span-4 lg:col-span-3 space-y-4">
                                <TextField
                                    type="text"
                                    name="name"
                                    id="name"
                                    label='Name'
                                    value={data.name}
                                    onChange={onChange('name')}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                                    error={validation.hasErrors('name')}
                                    help={validation.getErrors('name')[0]}
                                />

                                <TextField
                                    type="text"
                                    name="website"
                                    id="website"
                                    label='Website'
                                    hint='Optional'
                                    value={data.website}
                                    onChange={onChange('website')}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                                    error={validation.hasErrors('website')}
                                    help={validation.getErrors('website')[0]}
                                />

                                <Feature when={features => ! features.settingsTimezone}>
                                    <Combobox
                                        label='Timezone'
                                        options={timezones}
                                        value={data.timezone}
                                        onChange={onChange('timezone')}
                                    />
                                </Feature>
                            </div>
                        </fieldset>
                    )}
                </Form>
                <div className="flex justify-end">
                    <Button form='account-details' type='primary'>Save</Button>
                </div>
            </SettingsPanel>

            <Feature when={features => features.settingsSocial}>
                <SettingsPanel title='Social Profiles' description='Links to your social media profiles.'>
                    <Form id='social-profiles' validate={socialProfilesForm} onSubmit={() => console.log('Handle Social Profiles...')}>
                        {(data, onChange, validation) => (
                            <fieldset className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6">

                                <div className="sm:col-span-4 lg:col-span-3 space-y-4">
                                    <TextField
                                        type="text"
                                        name="facebook"
                                        id="facebook"
                                        label='Facebook'
                                        hint='Optional'
                                        placeholder='https://facebook.com/you'
                                        onChange={onChange('facebook')}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                                        error={validation.hasErrors('facebook')}
                                        help={validation.getErrors('facebook')[0]}
                                    />

                                    <TextField
                                        type="text"
                                        name="twitter"
                                        id="twitter"
                                        label='Twitter'
                                        hint='Optional'
                                        placeholder='https://twitter.com/you'
                                        onChange={onChange('twitter')}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                                        error={validation.hasErrors('twitter')}
                                        help={validation.getErrors('twitter')[0]}
                                    />

                                    <TextField
                                        type="text"
                                        name="instagram"
                                        id="instagram"
                                        label='Instagram'
                                        hint='Optional'
                                        placeholder='https://instagram.com/you'
                                        onChange={onChange('instagram')}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                                        error={validation.hasErrors('instagram')}
                                        help={validation.getErrors('instagram')[0]}
                                    />
                                </div>
                            </fieldset>
                        )}
                    </Form>
                    <div className="flex justify-end">
                        <Button form='social-profiles' type='primary'>Save</Button>
                    </div>
                </SettingsPanel>
            </Feature>

            <Feature when={features => features.settingsTeam}>
                <SettingsPanel title='Team Members' description='Invite your team and collaborate together!'>
                    <Form defaults={{email: ''}} id='invite-team-members' validate={inviteTeamMemberForm} onSubmit={this.onInvite}>
                        {(data, onChange, validation) => (
                            <fieldset className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6 flex">

                                <div className="sm:col-span-4 lg:col-span-3 space-y-4 flex items-end">
                                    <div className='flex-grow'>
                                        <TextField
                                            type="email"
                                            name="email"
                                            id="email"
                                            label='Add team member'
                                            placeholder='Email address'
                                            value={data.email}
                                            onChange={onChange('email')}
                                            className="flex-grow mt-1 block w-full border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm focus:ring-blue-500 focus:border-blue-500"
                                            error={validation.hasErrors('email')}
                                            help={validation.getErrors('email')[0]}
                                        />
                                    </div>
                                    <Button form='invite-team-members' type='primary' className='ml-4 flex-grow-0'>Invite</Button>
                                </div>
                            </fieldset>
                        )}
                    </Form>
                    {
                        <TeamMemberList members={this.props.users} invites={this.props.invites} />
                    }
                </SettingsPanel>
            </Feature>

        </Container>;
    }
}

const mapStateToProps = (state, ownProps) => ({
    accountId: state.currentAccountId,
    account: state.accounts[state.currentAccountId],

    invites: state.invites,
    users: state.users,
});

const mapDispatchToProps = {
    updateDetails: AccountActions.updateDetails,
    invite: UserActions.invite
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SettingsController);
